import { FilteredCreditApplication, getBackendUrl, reportErrorToConsole } from '@applyonline/shared'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDownloadVouchedReport, usePrequalify } from '../../api/credit-api'
import {
  IdentityVerificationSession,
  useResetIdentityVerificationSession,
  useUpdateIdentityVerificationSession,
} from '../../api/identity-verification-session-api'
import { VouchedConfig } from '../../types/vouched/vouched-types'
import { isJobValid } from './vouched-selectors'

export const useScript = (src: string) => {
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => setLoaded(true)
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [src])

  return loaded
}

export const useDefaultConfig = (
  creditApp: FilteredCreditApplication,
  session: IdentityVerificationSession | null,
  imgSrc: string,
) => {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const [prequalify] = usePrequalify()
  const [downloadVouchedReport] = useDownloadVouchedReport()
  const [setSession] = useUpdateIdentityVerificationSession()
  const resetIdentityVerificationSession = useResetIdentityVerificationSession(creditApp.id)
  const { t } = useTranslation()
  const url = getBackendUrl(import.meta.env.PROD, import.meta.env.VITE_BACKEND_API_URL)

  const config = useMemo((): VouchedConfig => {
    return {
      onInit: (args) => {
        if (args.token !== session?.token) {
          setSession({ id: creditApp.id, token: args.token }).catch(reportErrorToConsole)
        }
      },
      onDone: (job) => {
        const replace = { replace: true }
        resetIdentityVerificationSession()
        if (isJobValid(job)) {
          prequalify({ creditApplicationId: creditApp.id, vouchedJobToken: job.token })
            .then(() => navigate(`/creditApplication/${creditApp.id}`, replace))
            .catch(reportErrorToConsole)
        } else {
          downloadVouchedReport({ creditApplicationId: creditApp.id, vouchedJobToken: job.token }).catch(
            reportErrorToConsole,
          )
          navigate(`/creditApplication/${creditApp.id}/failedid`, replace)
        }
      },
      onCamera: (args) => {
        if (args.hasPermission === false) {
          navigate(`/creditApplication/${creditApp.id}/cameraPermission`, { replace: true })
        }
      },
      token: session?.token,
      appId: import.meta.env.VITE_VOUCHED_PUBLIC_API_KEY,
      callbackURL: `${url}/${creditApp.financingProgramId}/creditapplication/${creditApp.id}/VouchedCallback`,
      verification: {
        firstName: creditApp.applicant.firstName,
        lastName: creditApp.applicant.hasLegalName ? '' : creditApp.applicant.lastName,
      },
      content: {
        progressIndicatorLoading: 'loading',
        progressIndicatorVouching: 'loading',
        qrHandoffInstructions: t('vouched.content.qrHandoffInstructions'),
        upperStartInstructions: t('vouched.content.upperStartInstructions'),
        middleStartInstructions: ' ',
        crossDeviceInstructions: t('vouched.content.crossDeviceInstructions'),

        // Disable vouched using a desktop or laptop webcam.
        // Both need to be a blank space. If left completely empty, a default text will be used.
        qrDesktopLink: ' ',
        qrDesktopInstructions: ' ',
      },
      locale: i18n.language,
      liveness: 'enhanced',
      idLiveness: 'distance',
      id: 'camera',
      face: 'camera',
      maxRetriesBeforeNext: 99,
      numForceRetries: 99,
      manualCaptureTimeout: 360000,
      showProgressBar: true,
      showUploadFirst: true,
      enableEyeCheck: true,
      includeBackId: true,
      includeBarcode: false,
      crossDevice: true,
      crossDeviceQRCode: true,
      crossDeviceSMS: true,
      userConfirmation: {
        confirmData: true,
        confirmImages: true,
      },
      theme: {
        name: 'avant',
        iconLabelColor: '#413d3a',
        bgColor: '#FFFFFF',
        navigationActiveText: '#08ABF0',
        iconColor: '#f6f5f3',
        iconBackground: '#08ABF0',
        baseColor: '#08ABF0',
        fontColor: '#413d3a',
        font: 'Open Sans',
        navigationActiveBackground: '#08ABF0',
        navigationDisabledBackground: '#e0e0e0',
        navigationDisabledText: '#a0a0a0',
        secondaryButtonColor: 'rgb(216, 216, 216)',
        handoffLinkColor: '#08ABF0',
        progressIndicatorTextColor: '#000',
        logo: {
          src: imgSrc,
          style: { maxWidth: '300px', marginBottom: '20px' },
        },
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language])

  return { config }
}
